import { useMemo, useState } from "react";
import { Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@onengine/component-library";
import { FormBuilderQueryKeys, getFormBuilderDetail } from "@/api/form-builder/formBuilder.queries";
import { useSaveFormDetails } from "@/hooks/form-builder/useFormLifecycleMutations";
import { compressIfNeeded } from "@/utils/compressPayload";
import Loader from "@/components/ui/Loader/Loader";
import { Modal } from "@/components/ui/Modal/Modal";
import {
  NewFormTemplateModal,
  type FormDetailsInitialValues,
  type NewFormTemplateValues,
} from "../NewFormTemplateModal/NewFormTemplateModal";
import { isFormNameTaken } from "@/api/form-builder/formNameTaken";
import type {
  FormBuilderDetailData,
  FormBuilderListItem,
} from "@/api/form-builder/formBuilder.types";

interface EditFormDetailsModalProps {
  form: FormBuilderListItem;
  tenantId?: string;
  onClose: () => void;
}

const MODAL_TITLE = "Edit Form Details";

const toInitialValues = (detail: FormBuilderDetailData): FormDetailsInitialValues => ({
  name: detail.name,
  formTypeId: detail.formType ? String(detail.formType.id) : "",
  workOrderTypeId: detail.workOrderType ? String(detail.workOrderType.id) : "",
  tradeIds: (detail.trades ?? []).map((trade) => String(trade.id)),
  subTradeIds: (detail.subTrades ?? []).map((trade) => String(trade.id)),
  levelIds: (detail.levels ?? []).map((level) => String(level.id)),
  description: detail.description ?? "",
});

export function EditFormDetailsModal({ form, tenantId, onClose }: EditFormDetailsModalProps) {
  const {
    data: detail,
    isError,
    refetch,
  } = useQuery({
    queryKey: [FormBuilderQueryKeys.detail, "editDetails", form.id, tenantId],
    queryFn: ({ signal }) => getFormBuilderDetail(form.id, tenantId, signal),
  });
  const saveDetails = useSaveFormDetails();
  const [takenName, setTakenName] = useState<string>();
  const initialValues = useMemo(() => (detail ? toInitialValues(detail) : undefined), [detail]);

  if (!detail || detail.readOnly) {
    return (
      <Modal isOpen onClose={onClose} title={MODAL_TITLE} size={640}>
        {detail?.readOnly && <Text size="sm">{"This form can't be edited here."}</Text>}
        {!detail && isError && (
          <Stack gap="sm" align="flex-start" role="alert">
            <Text size="sm">{"Couldn't load this form's details."}</Text>
            <Button variant="secondary" size="small" onClick={() => void refetch()}>
              Try again
            </Button>
          </Stack>
        )}
        {!detail && !isError && (
          <div role="status" aria-label="Loading form details">
            <Loader />
          </div>
        )}
      </Modal>
    );
  }

  const handleSubmit = async (values: NewFormTemplateValues) => {
    if (saveDetails.isPending) return;
    // The save endpoint rewrites the draft, so it must carry the form's current fields.
    const { value, isCompressed } = await compressIfNeeded(JSON.stringify(detail.structure));
    saveDetails.mutate(
      {
        id: detail.id,
        name: values.name,
        structure: isCompressed ? value : detail.structure,
        isCompressed,
        status: "DRAFT",
        ...(tenantId ? { tenantId } : {}),
        formTypeId: Number(values.formTypeId),
        workOrderTypeId: Number(values.workOrderTypeId),
        tradeIds: values.tradeIds.map(Number),
        subTradeIds: values.subTradeIds.map(Number),
        levelIds: values.levelIds.map(Number),
        description: values.description,
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (isFormNameTaken(error)) {
            setTakenName(values.name);
          }
        },
      },
    );
  };

  return (
    <NewFormTemplateModal
      opened
      mode="edit"
      initialValues={initialValues}
      warnOnSlotChange={detail.status === "PUBLISHED"}
      isSubmitting={saveDetails.isPending}
      takenName={takenName}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  );
}
